// The blackjack screen (casino.md §4.2).
//
// **The client rules on nothing.** `CasinoTable.allowed_actions` states
// which of HIT, STAND, DOUBLE, SPLIT, INSURE and DECLINE_INSURANCE the
// reader's seat may take, and every button here is disabled unless the
// frame named its act. No chart, no dealer rule and no split rule lives
// in this file. `handValue` below is the one sum the screen owns, and it
// is DISPLAY ONLY: it puts a number on a hand the reader can already
// count, and the server settles the hand.
//
// **The dealer's hole card is hidden by the server, not by the screen.**
// `casino_wire.go` sends the byte 0 for a card the reader may not read,
// and it fills `dealer_cards` at the reveal. So the screen draws the
// back while the frame carries fewer than two dealer cards, and it never
// holds a card it then declines to draw.
//
// **A split shows as a second seat view.** The wire gives one seat one
// `cards` list, so the server seats the split hand beside the first one
// and marks both `is_you`. The screen therefore draws every own seat
// side by side and needs no second card list of its own.
//
// The turn bar steps once a second (§10.3). A bar that drained smoothly
// would need one style write a frame into an element this screen does not
// own — `GameView.html` returns a string — and the whole panel's markup
// would change four times a second under the reader's finger. So the
// fraction is quantised to the whole second the countdown states, the
// markup changes one time a second at most, and no interval runs.

import { overlay as S } from '../../gen/strings';
import type { MapKeyId } from '../../gen/presentation';
import { CASINO_CHIP_STEPS, CASINO_TIMER_WARN_FRAC } from '../../gen/presentation';
import {
  CasinoAction, CasinoPhase, type CasinoResult, type CasinoSeatView,
  type CasinoTable,
} from '../../gen/proto/aquaphobia';
import { groupThousands } from '../../locale/group';
import { esc } from '../esc';
import { card, cardRank, cardText, cardValid, cards, ctl, seatData, seatName, stakeRow } from './cards';
import {
  casinoActFrac, casinoActLeftSec, casinoOffers, casinoOwnRows, casinoSeatsOf, tableMinBet,
} from './casinostate';
import { seatBadge } from './club';
import { part, type GameCtx, type GameView } from './gameview';
import { keyTitle } from './help';
import { tagSpeech, resultTag, handWord, sayAttrs, wonSeats } from './moment';

/** What a hand is worth, and whether an ace still counts as eleven. */
export interface HandValue {
  total: number;
  soft: boolean;
}

/**
 * What a hand of card bytes is worth.
 *
 * An ace is one or eleven, a face card is ten, and the byte 0 is no card
 * at all. The sum counts every ace as one first, then lifts ONE ace to
 * eleven while that still fits under twenty-two; a second lifted ace
 * would always bust, so the rule needs no loop. A hand that reads soft
 * holds an ace the player may still drop back to one.
 *
 * This is display only. The server settles every hand.
 */
export function handValue(cards: number[]): HandValue {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    if (!cardValid(c)) continue;
    const rank = cardRank(c);
    if (rank === 1) {
      aces += 1;
      total += 1;
    } else {
      total += Math.min(rank, 10);
    }
  }
  if (aces > 0 && total + 10 <= 21) return { total: total + 10, soft: true };
  return { total, soft: false };
}

/**
 * True while the frame named this act AND the reader's seat holds the
 * turn (R988).
 *
 * The turn is read here and not in the markup alone, because a server
 * that states a stale `allowed_actions` would otherwise leave HIT and
 * STAND live at a seat that is not at turn. The wire says both facts, and
 * a button needs both: an act nobody may take must be grey, and a press
 * that slipped through must send nothing.
 */
function allows(t: CasinoTable, a: CasinoAction): boolean {
  return myTurn(t) && casinoOffers(t, a);
}

/** True while one of the reader's own seats holds the turn. */
function myTurn(t: CasinoTable): boolean {
  return casinoSeatsOf(t).some((s) => s.seat === t.turnSeat);
}

/** How many cards of the dealer's hand stand face up. */
function dealerRevealed(t: CasinoTable): number[] {
  return t.dealerCards.filter(cardValid);
}

/** One action button of the bar, disabled unless the frame allowed it.
 *  A button with a key names its cap in its title (R2143). */
function barButton(t: CasinoTable, op: string, a: CasinoAction, label: string, key?: MapKeyId): string {
  return ctl(op, label, allows(t, a), '', key ? keyTitle(label, key) : '');
}

/** The chip that states what one hand is worth, for the reader's own hand. */
function ownValueChip(s: CasinoSeatView): string {
  const v = handValue(s.cards);
  if (v.total === 0) return '';
  if (v.total > 21) return `<span class="chip bust">${S.bj_bust}</span>`;
  if (s.cards.length === 2 && v.total === 21) {
    return `<span class="chip nat">${S.bj_natural}</span>`;
  }
  const soft = v.soft ? ' soft' : '';
  return `<span class="chip${soft}">${S.bj_you_have({ value: groupThousands(v.total) })}</span>`;
}

/** The chip of another seat: the figure alone, because the sentence is the reader's. */
function seatValueChip(s: CasinoSeatView): string {
  const v = handValue(s.cards);
  if (v.total === 0) return '';
  const soft = v.soft ? ' soft' : '';
  return `<span class="chip${soft}">${groupThousands(v.total)}</span>`;
}

/**
 * One seat of the ring: who sits there, the chips, the bet and the cards.
 * A seat that beat the dealer in the result on view wears the win flash of
 * that result (`flash`, R2115).
 */
function seatHtml(t: CasinoTable, s: CasinoSeatView, won: ReadonlySet<number>, flash: number): string {
  const big = s.isYou || s.seat === t.yourSeat;
  const cls = ['seat'];
  if (big) cls.push('you');
  if (t.turnSeat === s.seat) cls.push('turn');
  if (s.folded || s.sittingOut) cls.push('folded');
  const win = won.has(s.seat);
  if (win) cls.push('won');
  const hand = cards(s.cards, { big });
  // Blackjack is a HOUSE game: a seat bets against the dealer and holds
  // no stack at the table, so `chips` is 0 by design and a line that read
  // "chips 0" stated a fact that is not one. The seat prints what it has
  // on this hand instead (R988). Hold'em is the player game, and
  // `poker.ts` still prints the stack there.
  const money = s.bet > 0
    ? `<div class="hold bet">${groupThousands(s.bet)}</div>` : '';
  // The bot and fictive flags ride on the root for the operator view (R1352).
  // The name wears the faction badge of the seat on its left, as a hold'em
  // seat and a rim seat do; a fictive seat carries faction 0 and wears
  // none (R2165, R2198). The name keeps its one line and its ellipsis.
  return `<div class="${cls.join(' ')}"${seatData(s)}${win ? ` data-flash="${flash}"` : ''}>`
    + `<div class="who">${seatBadge(s.faction)}<span class="nm">${seatName(s)}</span></div>`
    + money
    + `<div class="hand">${hand}</div>`
    + `<div class="marks">${big ? ownValueChip(s) : seatValueChip(s)}</div>`
    + '</div>';
}

/** The dealer's row: the up card, the hidden hole card and the reveal. */
function dealerHtml(t: CasinoTable): string {
  const shown = dealerRevealed(t);
  let hand: string;
  let says = '';
  if (shown.length >= 2) {
    hand = cards(shown);
    const v = handValue(shown);
    says = `<span class="chip">${groupThousands(v.total)}</span>`;
  } else if (t.dealerUp > 0) {
    // The hole card is the byte the server withheld. It draws as a back
    // and the sentence states the one card the table may read.
    hand = card(t.dealerUp) + `<span class="hole">${card(0, { hidden: true })}</span>`;
    says = `<span class="says">${S.bj_dealer_shows({ card: cardText(t.dealerUp) })}</span>`;
  } else {
    return '';
  }
  return `<div class="board dealer"><div class="hand">${hand}</div>`
    + `<div class="marks">${says}</div></div>`;
}

/** The turn bar: whose turn it is, how long is left and the stepped bar. */
function turnHtml(ctx: GameCtx, t: CasinoTable): string {
  if (t.phase !== CasinoPhase.CASINO_PHASE_ACTING || t.actLeftSec <= 0) return '';
  const mine = myTurn(t);
  const left = Math.ceil(casinoActLeftSec(ctx.slice, ctx.nowMs));
  // The fraction is taken from the whole second the reader sees, so the
  // markup and the bar step together and neither churns between seconds.
  const frac = Math.min(1, left / t.actLeftSec);
  const warn = casinoActFrac(ctx.slice, ctx.nowMs) <= CASINO_TIMER_WARN_FRAC ? ' warn' : '';
  const lbl = mine ? S.casino_your_turn : S.casino_turn_left({ sec: groupThousands(left) });
  return part('turn', `class="turnbar${mine ? ' mine' : ''}"`,
    `<span class="lbl">${lbl}</span>`
    + `<div class="casino-timer${warn}">`
    + `<span class="timer-fill" style="transform: scaleX(${frac.toFixed(3)})"></span>`
    + '</div>');
}

/** The stake pills and PLACE BET of the betting window. */
function betHtml(ctx: GameCtx, t: CasinoTable): string {
  if (t.phase !== CasinoPhase.CASINO_PHASE_BETTING) return '';
  // The betting window is SHARED: every box bets inside one span and no
  // seat holds a turn, so the row reads the offer alone (casino.md §4.2).
  if (!casinoOffers(t, CasinoAction.CASINO_ACTION_PLACE_BET)) return '';
  const min = tableMinBet(ctx.rules, t);
  if (min <= 0) return '';
  // One pill group of the choice kit; a stake over the credits is shut
  // (R2118).
  const pills = stakeRow(BET_STEPS.map((m) => ({
    id: `bj-${m}`,
    label: groupThousands(min * m),
    payable: min * m <= ctx.credits,
    data: { op: 'stake', mult: String(m) },
  })), `bj-${stakeStep}`, true);
  const amount = min * stakeStep;
  return part('bet', 'class="betrow"',
    `<div class="note">${S.casino_min_bet({ amount: groupThousands(min) })}</div>`
    + `<div class="pills">${pills}</div>`
    // The locale pack carries the label of the press that spends at a
    // table, and the amount stands on the pill beside it.
    + `<div class="casino-bar">${ctl('bet', S.casino_place_bet, amount <= ctx.credits, 'primary',
      keyTitle(S.casino_place_bet, 'casino_bet'))}</div>`);
}

/** The insurance pair. It draws only while the frame offers the act. */
function insuranceHtml(ctx: GameCtx, t: CasinoTable): string {
  if (!allows(t, CasinoAction.CASINO_ACTION_INSURE)) return '';
  const own = casinoSeatsOf(t)[0];
  const cost = Math.floor((own?.bet ?? 0) / 2);
  const pays = ctx.rules?.insurancePays ?? 0;
  return part('insure', 'class="insure"',
    `<div class="ask">${S.bj_insurance_ask}</div>`
    + `<div class="cost">${S.bj_insurance_cost({
      amount: groupThousands(cost), pays: groupThousands(pays),
    })}</div>`
    + `<div class="casino-bar">${ctl('insure', S.bj_yes, true, 'primary')}`
    + `${ctl('noinsure', S.bj_no)}</div>`);
}

/**
 * The result on view: the last settled hand of this table, until a card
 * of the next hand falls. A reader who sees "you lost" over two fresh
 * cards reads it as the new hand's answer (R988). The result stands
 * through the betting window, so a reader who looked away still reads
 * what the last hand paid.
 */
function shownResult(ctx: GameCtx, t: CasinoTable): CasinoResult | null {
  const r: CasinoResult | null = ctx.slice.result;
  if (!r || r.tableId !== t.id) return null;
  // The server keeps the cards of the last hand on the felt through the
  // betting window, so a card alone does not mark the next hand: the deal
  // does, and the deal ends the window.
  if (r.handId !== t.handId && t.phase !== CasinoPhase.CASINO_PHASE_BETTING && dealt(t)) return null;
  return r;
}

/**
 * What the settled hand paid, and the line the server wrote for it.
 *
 * The reader's own move is the result tag of `moment.ts` (R2115): a move
 * of 0 reads PUSH. A hand the reader had no stake on draws no result tag, and a
 * dissolved table states that every stake came back. The part is the
 * result line the live region says.
 */
function resultHtml(ctx: GameCtx, t: CasinoTable): string {
  const r = shownResult(ctx, t);
  if (!r) return '';
  const parts: string[] = [];
  if (r.line) parts.push(`<div class="line">${esc(r.line)}</div>`);
  const mine = casinoOwnRows(r, t);
  for (const s of mine) {
    if (s.handRankLine) parts.push(`<div class="rank">${esc(s.handRankLine)}</div>`);
  }
  const seq = ctx.slice.resultSeq;
  let speech: string;
  if (r.aborted) {
    speech = S.casino_stakes_back;
    parts.push(`<div class="nobet">${speech}</div>`);
  } else if (mine.length === 0) {
    speech = S.casino_no_bet;
    parts.push(`<div class="nobet">${speech}</div>`);
  } else {
    const word = handWord(r.yourDelta, true);
    speech = tagSpeech(r.yourDelta, word);
    parts.push(`<div class="delta">${resultTag(r.yourDelta, word, seq)}</div>`);
  }
  return part('result', `class="result"${sayAttrs(seq, speech)}`, parts.join(''));
}

/** True once a card of the new hand stands on the felt. */
function dealt(t: CasinoTable): boolean {
  if (t.dealerUp > 0 || t.dealerCards.length > 0) return true;
  return t.seats.some((s) => s.cards.length > 0);
}

/**
 * The four stake presets, as multiples of the tier's minimum bet.
 *
 * The multiples belong to `data/presentation/presentation.toml`, so this
 * bar and the roulette felt offer one row of chips (R988).
 */
const BET_STEPS = CASINO_CHIP_STEPS;

/**
 * The preset the reader picked, as a multiple of the minimum bet.
 *
 * This is the reader's intent before the wire sees it, so the screen may
 * hold it (`gameview.ts`). It survives a table frame, because a frame
 * must not move a pill under the finger.
 */
let stakeStep = BET_STEPS[0];

/**
 * PLACE BET: one bet of the picked preset, while the shared window offers
 * the act and the credits pay it. The button and the key `casino_bet`
 * take this one road (R2143).
 */
function placeBet(ctx: GameCtx, t: CasinoTable): boolean {
  if (t.phase !== CasinoPhase.CASINO_PHASE_BETTING || !casinoOffers(t, CasinoAction.CASINO_ACTION_PLACE_BET)) return false;
  const amount = tableMinBet(ctx.rules, t) * stakeStep;
  if (amount <= 0 || amount > ctx.credits) return false;
  ctx.send({ kind: 'act', action: CasinoAction.CASINO_ACTION_PLACE_BET, amount });
  return true;
}

/** Sends one blackjack act. Every act of this screen takes no amount. */
function act(ctx: GameCtx, action: CasinoAction): void {
  ctx.send({ kind: 'act', action });
}

/** The act each button of the bar and the insurance pair sends. */
const OP_ACTION: ReadonlyMap<string, CasinoAction> = new Map([
  ['hit', CasinoAction.CASINO_ACTION_HIT],
  ['stand', CasinoAction.CASINO_ACTION_STAND],
  ['double', CasinoAction.CASINO_ACTION_DOUBLE],
  ['split', CasinoAction.CASINO_ACTION_SPLIT],
  ['insure', CasinoAction.CASINO_ACTION_INSURE],
  ['noinsure', CasinoAction.CASINO_ACTION_DECLINE_INSURANCE],
]);

/** The act each casino keybinding sends. */
const KEY_ACTION: Partial<Record<MapKeyId, CasinoAction>> = {
  casino_hit: CasinoAction.CASINO_ACTION_HIT,
  casino_stand: CasinoAction.CASINO_ACTION_STAND,
  casino_double: CasinoAction.CASINO_ACTION_DOUBLE,
};

export const blackjackView: GameView = {
  html(ctx: GameCtx): string {
    const t = ctx.slice.table;
    if (!t) return '';
    const shown = shownResult(ctx, t);
    const won = wonSeats(shown);
    const ring = t.seats.map((s) => seatHtml(t, s, won, ctx.slice.resultSeq)).join('');
    // The parts of the felt (`gameview.ts`). The turn bar steps once a
    // second, so it stands alone and the cards beside it are not rewritten
    // under the reader's thumb.
    return `<div class="casino-felt bj${ctx.still ? ' still' : ''}">`
      + part('felt', 'class="felt"',
        dealerHtml(t) + `<div class="seat-ring">${ring}</div>`)
      + turnHtml(ctx, t)
      + betHtml(ctx, t)
      + insuranceHtml(ctx, t)
      + part('acts', 'class="casino-bar"',
        barButton(t, 'hit', CasinoAction.CASINO_ACTION_HIT, S.bj_hit, 'casino_hit')
        + barButton(t, 'stand', CasinoAction.CASINO_ACTION_STAND, S.bj_stand, 'casino_stand')
        + barButton(t, 'double', CasinoAction.CASINO_ACTION_DOUBLE, S.bj_double, 'casino_double')
        + barButton(t, 'split', CasinoAction.CASINO_ACTION_SPLIT, S.bj_split))
      + resultHtml(ctx, t)
      // The house rules stand in the help fold at the head of the screen
      // (R2143), the one home of the facts of this game.
      + '</div>';
  },

  act(op: string, btn: HTMLButtonElement, _key: string, ctx: GameCtx): boolean {
    const t = ctx.slice.table;
    if (!t) return false;
    switch (op) {
      case 'stake': {
        const m = Number(btn.dataset.mult ?? '0');
        if (BET_STEPS.includes(m)) stakeStep = m;
        return true;
      }
      case 'bet':
        placeBet(ctx, t);
        return true;
      default: {
        // Every other button sends one act, and only while the frame
        // allowed it. The button takes the release either way.
        const action = OP_ACTION.get(op);
        if (action === undefined) return false;
        if (allows(t, action)) act(ctx, action);
        return true;
      }
    }
  },

  key(id: MapKeyId, ctx: GameCtx): boolean {
    const t = ctx.slice.table;
    if (!t) return false;
    // A key takes the same road a button takes: it acts only on an act
    // the frame named, and it answers false otherwise, so the press falls
    // through to whatever else claims the letter. The bet key places the
    // bet of the picked preset, as PLACE BET does (R2143).
    if (id === 'casino_bet') return placeBet(ctx, t);
    const action = KEY_ACTION[id];
    if (action === undefined || !allows(t, action)) return false;
    act(ctx, action);
    return true;
  },
};

/** Puts the betting preset back to the smallest one. For a test alone. */
export function resetBlackjackStakeStep(): void {
  stakeStep = BET_STEPS[0];
}
